'use client';

import React, { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { ProfileFormData } from './CreateProfileWizard';

interface Props {
  form: UseFormReturn<ProfileFormData>;
}

const bioMaxLength = 160;

export default function ProfileStep1({ form }: Props) {
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const { register, formState: { errors }, watch } = form;
  const bioValue = watch('bio') || '';

  const checkUsername = async (value: string) => {
    if (value.length < 3) { setUsernameStatus('idle'); return; }
    setUsernameStatus('checking');
    // BACKEND INTEGRATION: GET /api/check-username?username=value
    await new Promise((r) => setTimeout(r, 500));
    setUsernameStatus(['priya', 'admin', 'biolink', 'test'].includes(value.toLowerCase()) ? 'taken' : 'available');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Basic Information</h2>
        <p className="text-sm text-muted-foreground">This is what visitors see when they open your bio page.</p>
      </div>

      {/* Username */}
      <div>
        <label htmlFor="cp-username" className="block text-sm font-600 text-foreground mb-1">
          Username
          <span className="text-xs text-muted-foreground font-400 ml-2">— your public URL</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">Only lowercase letters, numbers, dots and underscores</p>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-500 pointer-events-none">
            biolink.app/
          </span>
          <input
            id="cp-username"
            type="text"
            className={`w-full pl-28 pr-10 py-3 rounded-xl border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
              errors.username ? 'border-danger' : 'border-border'
            }`}
            {...register('username', {
              required: 'Username is required',
              minLength: { value: 3, message: 'Minimum 3 characters' },
              pattern: { value: /^[a-z0-9._]+$/, message: 'Only lowercase letters, numbers, dots and underscores' },
              onChange: (e) => checkUsername(e.target.value),
            })}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            {usernameStatus === 'checking' && (
              <svg className="w-4 h-4 text-muted-foreground animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {usernameStatus === 'available' && (
              <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {usernameStatus === 'taken' && (
              <svg className="w-4 h-4 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </span>
        </div>
        {errors.username && <p className="text-xs text-danger mt-1.5">{errors.username.message}</p>}
        {usernameStatus === 'available' && <p className="text-xs text-success mt-1.5">Username is available!</p>}
        {usernameStatus === 'taken' && <p className="text-xs text-danger mt-1.5">This username is already taken — try another</p>}
      </div>

      {/* Display name */}
      <div>
        <label htmlFor="cp-displayname" className="block text-sm font-600 text-foreground mb-1">
          Display Name
        </label>
        <p className="text-xs text-muted-foreground mb-2">Your full name or brand name as it appears on your page</p>
        <input
          id="cp-displayname"
          type="text"
          placeholder="e.g. Priya Sharma"
          className={`w-full px-4 py-3 rounded-xl border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
            errors.displayName ? 'border-danger' : 'border-border'
          }`}
          {...register('displayName', {
            required: 'Display name is required',
            maxLength: { value: 50, message: 'Maximum 50 characters' },
          })}
        />
        {errors.displayName && <p className="text-xs text-danger mt-1.5">{errors.displayName.message}</p>}
      </div>

      {/* Bio */}
      <div>
        <label htmlFor="cp-bio" className="block text-sm font-600 text-foreground mb-1">
          Bio
        </label>
        <p className="text-xs text-muted-foreground mb-2">Tell visitors who you are. Emojis are encouraged! 🎉</p>
        <textarea
          id="cp-bio"
          rows={4}
          placeholder="e.g. ✨ Content Creator & Lifestyle Blogger&#10;📍 Mumbai, India&#10;🎥 New video every Tuesday!"
          className={`w-full px-4 py-3 rounded-xl border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-none ${
            errors.bio ? 'border-danger' : 'border-border'
          }`}
          {...register('bio', {
            maxLength: { value: bioMaxLength, message: `Maximum ${bioMaxLength} characters` },
          })}
        />
        <div className="flex items-center justify-between mt-1">
          {errors.bio ? (
            <p className="text-xs text-danger">{errors.bio.message}</p>
          ) : (
            <span />
          )}
          <p className={`text-xs font-500 ${bioValue.length > bioMaxLength * 0.9 ? 'text-warning' : 'text-muted-foreground'}`}>
            {bioValue.length}/{bioMaxLength}
          </p>
        </div>
      </div>

      {/* Location */}
      <div>
        <label htmlFor="cp-location" className="block text-sm font-600 text-foreground mb-1">
          Location
          <span className="text-xs text-muted-foreground font-400 ml-2">— optional</span>
        </label>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </span>
          <input
            id="cp-location"
            type="text"
            placeholder="e.g. Mumbai, India"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
            {...register('location')}
          />
        </div>
      </div>
    </div>
  );
}